/**
 * <book-pair-add>: the "Add both to cart" button of the book pair offer
 * (snippets/book-pair-offer.liquid).
 *
 * One click adds both books in a single /cart/add.js request, then applies the BOGOS
 * bundle code (keeping any codes already on the cart), refreshes the header and cart
 * drawer from that same response, and finishes the way the theme's own Add to cart
 * does (added notification, cart drawer or cart page, per Theme settings).
 * Before this script runs (or without JavaScript) the form posts both books to
 * /cart/add itself, and its return_to applies the code through Shopify's discount link.
 */
if (!customElements.get('book-pair-add')) {
  class BookPairAdd extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.button = this.querySelector('button[type="submit"]');
      this.label = this.querySelector('.book-pair-btn__label');
      this.errorEl = this.querySelector('.book-pair-add__error');
      this.statusEl = this.querySelector('.book-pair-add__status');
      if (!this.form || !this.button) return;

      this.idleLabel = this.label ? this.label.textContent : '';
      this.handleSubmit = this.handleSubmit.bind(this);
      this.form.addEventListener('submit', this.handleSubmit);

      // A cached page can outlive the offer by a few minutes.
      if (this.hasEnded()) this.showEnded();
    }

    disconnectedCallback() {
      if (this.form) this.form.removeEventListener('submit', this.handleSubmit);
      clearTimeout(this.resetTimer);
    }

    hasEnded() {
      const endsAt = Number(this.dataset.endsAt) || 0;
      return endsAt > 0 && Date.now() / 1000 >= endsAt;
    }

    showEnded() {
      this.button.setAttribute('aria-disabled', 'true');
      this.showError(this.dataset.endedText);
    }

    async handleSubmit(evt) {
      evt.preventDefault();
      if (this.busy || this.button.getAttribute('aria-disabled') === 'true') return;
      if (this.hasEnded()) {
        this.showEnded();
        return;
      }

      this.busy = true;
      this.showError('');
      this.setState('loading');

      // After a tap whose code did not go on, the books are already in the cart, so the
      // next tap only retries the code instead of adding a second pair. If they have
      // left the cart since (the cart drawer, another tab), add them again.
      if (this.codePending && !(await this.pairInCart())) this.codePending = false;

      let added = null;
      if (!this.codePending) {
        // BOGOS groups one bundle's lines by _bogos_trigger. Its own widget uses a fresh
        // value for every add, so a second click makes a second bundle.
        const trigger = (this.dataset.triggerPrefix || '') + Math.random().toString(36).slice(2, 7);
        this.form.querySelectorAll('[data-bogos-trigger]').forEach((input) => {
          input.value = trigger;
        });
        const body = new FormData(this.form);
        body.delete('return_to'); // only for the form posting itself, see the snippet
        try {
          added = await this.post(window.theme.routes.cartAdd, body);
        } catch (error) {
          this.setState('');
          this.showError(error.message);
          this.busy = false;
          document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
          return;
        }
      }

      let updated = null;
      if (this.dataset.code) {
        // One retry, for a dropped or timed-out request. A code the cart refuses fails the
        // same way every time, so that is not retried.
        for (let attempt = 0; attempt < 2 && !updated; attempt += 1) {
          try {
            updated = await this.applyCode();
          } catch (error) {
            console.warn('Book pair: the bundle code was not applied.', error); // eslint-disable-line no-console
          }
        }
        this.codePending = !(updated && updated.applied);
        // A cart that answered but refused the code will refuse it again, so do not ask
        // the shopper to keep tapping; only failed requests are worth another try.
        this.codeRefused = Boolean(updated) && !updated.applied;
      }

      this.refreshCart(updated);
      if (added) {
        this.finish();
        this.dispatchEvent(new CustomEvent('on:cart:add', {
          bubbles: true,
          detail: { variantId: added.items && added.items[0] ? added.items[0].variant_id : null }
        }));
      }
      if (this.codePending) {
        this.setState('');
        this.showError(this.codeRefused ? this.dataset.codeRefusedText : this.dataset.codeErrorText);
        // Nothing this button can do will change a refusal, so it rests until the page reloads.
        if (this.codeRefused) this.button.setAttribute('aria-disabled', 'true');
      } else {
        this.setState('done');
      }
      this.busy = false;
    }

    async post(url, body) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      const isForm = body instanceof FormData;
      const headers = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
      if (!isForm) headers['Content-Type'] = 'application/json';
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers,
          body: isForm ? body : JSON.stringify(body),
          signal: controller.signal
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(typeof data.description === 'string' ? data.description : this.dataset.errorText);
        }
        return data;
      } catch (error) {
        if (error.name === 'AbortError' || error instanceof TypeError) throw new Error(this.dataset.errorText);
        throw error;
      } finally {
        clearTimeout(timeout);
      }
    }

    /**
     * Adds the BOGOS code to the codes already on the cart. The same request asks for the
     * header and cart drawer sections, so they show the discounted cart straight away.
     * @returns {Promise<{targets: HTMLElement[], sections: ?object, applied: boolean}>}
     */
    async applyCode() {
      const code = this.dataset.code;
      if (!code) return null;

      const targets = ['page-header', 'cart-drawer']
        .map((selector) => document.querySelector(selector))
        .filter((el) => el && el.dataset.sectionId && typeof el.updateFromCartChange === 'function');

      const cart = await this.getCart();
      const codes = (cart.discount_codes || []).map((discount) => discount.code).filter(Boolean);
      if (!codes.some((existing) => existing.toLowerCase() === code.toLowerCase())) codes.push(code);

      const data = await this.post(window.theme.routes.cartUpdate, {
        discount: codes.join(','),
        sections: targets.map((el) => el.dataset.sectionId).join(','),
        sections_url: window.location.pathname
      });
      const applied = (data.discount_codes || [])
        .some((discount) => discount.applicable && String(discount.code).toLowerCase() === code.toLowerCase());
      return { targets, sections: data.sections || null, applied };
    }

    /** Are both books still in the cart? Unknown counts as yes, so a retry never adds a second pair. */
    async pairInCart() {
      try {
        const cart = await this.getCart();
        const ids = Array.from(this.form.querySelectorAll('input[name$="[id]"]'), (input) => Number(input.value));
        return ids.every((id) => (cart.items || []).some((item) => item.variant_id === id));
      } catch (error) {
        return true;
      }
    }

    async getCart() {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(`${window.theme.routes.cart}.js`, {
          headers: { Accept: 'application/json' },
          signal: controller.signal
        });
        if (!response.ok) throw new Error(`Cart read failed (${response.status})`);
        return await response.json();
      } finally {
        clearTimeout(timeout);
      }
    }

    refreshCart(updated) {
      const sections = updated && updated.sections;
      const targets = updated ? updated.targets : [];
      const complete = sections && targets.length && targets.every((el) => sections[el.dataset.sectionId]);
      if (complete && !document.querySelector('.template-cart')) {
        targets.forEach((el) => el.updateFromCartChange(sections[el.dataset.sectionId]));
      } else {
        document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
      }
    }

    /** Ends the add the way the theme's own Add to cart does. */
    finish() {
      const after = window.theme.settings.afterAddToCart;
      if (document.querySelector('.template-cart')) return;

      if (after === 'page') {
        window.location.href = window.theme.routes.cart;
      } else if (after === 'drawer') {
        const drawer = document.querySelector('.js-cart-drawer');
        if (drawer) drawer.open(this.button);
      } else if (after === 'notification') {
        const template = document.getElementById('AddedNotification');
        if (template) {
          const notification = template.content.firstElementChild.cloneNode(true);
          notification.dataset.productTitle = this.dataset.titles || '';
          (document.querySelector('.pageheader--sticky') || document.body).appendChild(notification);
        }
      }
    }

    setState(state) {
      clearTimeout(this.resetTimer);
      this.button.classList.toggle('is-loading', state === 'loading');
      this.button.classList.toggle('is-done', state === 'done');
      if (state === 'loading') {
        this.button.setAttribute('aria-disabled', 'true');
        this.button.setAttribute('aria-busy', 'true');
      } else {
        this.button.removeAttribute('aria-disabled');
        this.button.removeAttribute('aria-busy');
      }

      if (this.label) {
        let text = this.codePending && !this.codeRefused ? (this.dataset.retryLabel || this.idleLabel) : this.idleLabel;
        if (state === 'done') text = this.dataset.addedText || this.idleLabel;
        this.label.textContent = text;
      }
      if (this.statusEl) {
        this.statusEl.textContent = state === 'done' ? `${this.dataset.titles}: ${this.dataset.addedText}` : '';
      }
      if (state === 'done') {
        this.resetTimer = setTimeout(() => this.setState(''), 3000);
      }
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || '';
      this.errorEl.hidden = !message;
    }
  }

  customElements.define('book-pair-add', BookPairAdd);
}
